// ============================================
// src/components/meetings/MeetingManagement.js
// Complete Meeting Management Module with Send Invite and Action Items Manager
// ============================================
// src/components/meetings/MeetingManagement.js
// Complete Meeting Management Module with Send Invite and Action Items Manager
// Updated with sidebar navigation while keeping ALL existing functionality

import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';
import MeetingScheduler from './MeetingScheduler';
import MeetingLive from './MeetingLive';
import ActionItemsManagement from './ActionItemsManagement';
import '../../pages/UserDashboard.css';
import './MeetingManagement.css';

function MeetingManagement({ userData, onBack }) {
  const [meetings, setMeetings] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [pastMeetings, setPastMeetings] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showActionItemsManager, setShowActionItemsManager] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');

  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';

  useEffect(() => {
    fetchMeetings();
    fetchActionItems();
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, [darkMode]);

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('meetings')
        .select('*')
        .order('meeting_date', { ascending: false });

      if (error) throw error;

      if (data) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const upcoming = data.filter(m => new Date(m.meeting_date) >= today);
        const past = data.filter(m => new Date(m.meeting_date) < today);

        setMeetings(data);
        setUpcomingMeetings(upcoming);
        setPastMeetings(past);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActionItems = async () => {
    try {
      const { data, error } = await supabase
        .from('action_items')
        .select('*, meetings(title, meeting_date)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setActionItems(data || []);
    } catch (error) {
      console.error('Error fetching action items:', error);
    }
  };

  const sendMeetingInvite = (meeting) => {
    const meetingDate = new Date(meeting.meeting_date);
    const dayName = meetingDate.toLocaleDateString('en-US', { weekday: 'long' });
    const formattedDate = meetingDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });

    let inviteMessage = `🏢 *SOCIETY MEETING INVITATION* 🏢\n\n`;
    inviteMessage += `Dear Residents,\n\n`;
    inviteMessage += `You are cordially invited to attend the *${meeting.title}*\n\n`;
    inviteMessage += `📅 *Date:* ${dayName}, ${formattedDate}\n`;
    
    if (meeting.meeting_time) {
      const [hours, minutes] = meeting.meeting_time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      inviteMessage += `⏰ *Time:* ${displayHour}:${minutes} ${ampm}\n`;
    }
    
    inviteMessage += `📍 *Venue:* ${meeting.venue}\n`;
    inviteMessage += `📋 *Type:* ${meeting.meeting_type?.replace('_', ' ').toUpperCase()}\n\n`;
    
    if (meeting.agenda) {
      inviteMessage += `*AGENDA:*\n`;
      inviteMessage += `${meeting.agenda}\n\n`;
    }
    
    inviteMessage += `✅ Your presence and participation are valuable for community decisions.\n`;
    inviteMessage += `✅ Please be on time.\n`;
    inviteMessage += `✅ Kindly inform if you cannot attend.\n\n`;
    inviteMessage += `Thank you!\n`;
    inviteMessage += `_Society Management Committee_`;
    
    const encodedMessage = encodeURIComponent(inviteMessage);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const whatsappURL = isMobile 
      ? `whatsapp://send?text=${encodedMessage}`
      : `https://web.whatsapp.com/send?text=${encodedMessage}`;
    
    navigator.clipboard.writeText(inviteMessage).then(() => {
      window.open(whatsappURL, '_blank');
      setTimeout(() => {
        alert('Meeting invitation copied and WhatsApp opened! Select the group to share the invitation.');
      }, 500);
    }).catch(err => {
      const textarea = document.createElement('textarea');
      textarea.value = inviteMessage;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      window.open(whatsappURL, '_blank');
      setTimeout(() => {
        alert('Meeting invitation copied and WhatsApp opened! Select the group to share the invitation.');
      }, 500);
    });
  };

  const handleStartMeeting = (meeting) => {
    setActiveMeeting(meeting);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'scheduled': return '#ffc107';
      case 'in_progress': return '#17a2b8';
      case 'completed': return '#28a745';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getActionItemStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#ffc107';
      case 'in_progress': return '#17a2b8';
      case 'completed': return '#28a745';
      default: return '#6c757d';
    }
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  if (activeMeeting) {
    return (
      <MeetingLive 
        meeting={activeMeeting} 
        userData={userData} 
        onClose={() => {
          setActiveMeeting(null);
          fetchMeetings();
          fetchActionItems();
        }} 
      />
    );
  }

  if (showActionItemsManager) {
    return (
      <ActionItemsManagement 
        userData={userData} 
        onBack={() => {
          setShowActionItemsManager(false);
          fetchActionItems();
        }} 
      />
    );
  }

  return (
    <div className={`dashboard-container meeting-management-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">🏠</span>
            <span className="menu-label">Dashboard</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <button
            className={`menu-item ${activeTab === 'upcoming' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('upcoming');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📅</span>
            <span className="menu-label">Upcoming</span>
          </button>
          
          <button
            className={`menu-item ${activeTab === 'past' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('past');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📆</span>
            <span className="menu-label">Past Meetings</span>
          </button>
          
          <button
            className={`menu-item ${activeTab === 'actions' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('actions');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">✅</span>
            <span className="menu-label">Action Items</span>
          </button>
          
          {isAdmin && (
            <>
              <div className="sidebar-divider"></div>
              
              <button
                className="menu-item"
                onClick={() => {
                  setShowScheduler(true);
                  closeMobileMenu();
                }}
              >
                <span className="menu-icon">➕</span>
                <span className="menu-label">Schedule Meeting</span>
              </button>
              
              <button
                className="menu-item"
                onClick={() => {
                  setShowActionItemsManager(true);
                  closeMobileMenu();
                }}
              >
                <span className="menu-icon">📝</span>
                <span className="menu-label">Manage Actions</span>
              </button>
            </>
          )}
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">📅 Meetings</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="meeting-header">
            <h2 className="page-title">📅 Meeting Management</h2>
            {isAdmin && (
              <div className="header-actions">
                <button 
                  onClick={() => setShowScheduler(true)}
                  className="btn-primary"
                >
                  + Schedule Meeting
                </button>
                <button 
                  onClick={() => setShowActionItemsManager(true)}
                  className="btn-secondary"
                  style={{background: '#9b59b6'}}
                >
                  📝 Manage Action Items
                </button>
              </div>
            )}
          </div>

          {/* Stats Cards */}
          <div className="dashboard-grid stats-grid">
            <div className="dashboard-card stat-card gradient-purple">
              <div className="stat-content">
                <h3>📅</h3>
                <p className="stat-value">{upcomingMeetings.length}</p>
                <small>Upcoming Meetings</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-green">
              <div className="stat-content">
                <h3>✅</h3>
                <p className="stat-value">{pastMeetings.length}</p>
                <small>Past Meetings</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-yellow">
              <div className="stat-content">
                <h3>📋</h3>
                <p className="stat-value">{actionItems.filter(a => a.status === 'pending').length}</p>
                <small>Pending Actions</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-cyan">
              <div className="stat-content">
                <h3>🎯</h3>
                <p className="stat-value">{actionItems.filter(a => a.status === 'completed').length}</p>
                <small>Completed Actions</small>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="meeting-tabs">
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`tab-button ${activeTab === 'upcoming' ? 'active' : ''}`}
            >
              Upcoming Meetings
            </button>
            <button
              onClick={() => setActiveTab('past')}
              className={`tab-button ${activeTab === 'past' ? 'active' : ''}`}
            >
              Past Meetings
            </button>
            <button
              onClick={() => setActiveTab('actions')}
              className={`tab-button ${activeTab === 'actions' ? 'active' : ''}`}
            >
              Action Items
            </button>
          </div>

          {/* Content - KEEPING ALL YOUR ORIGINAL CONTENT */}
          {loading ? (
            <div className="dashboard-card">
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Upcoming Meetings - YOUR ORIGINAL CODE */}
              {activeTab === 'upcoming' && (
                <div className="dashboard-card">
                  <h3 style={{ marginBottom: '20px' }}>Upcoming Meetings</h3>
                  {upcomingMeetings.length > 0 ? (
                    <div style={{ display: 'grid', gap: '15px' }}>
                      {upcomingMeetings.map(meeting => (
                        <div key={meeting.id} className="meeting-item">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <h4 style={{ margin: '0 0 10px 0' }}>{meeting.title}</h4>
                              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                                📅 {new Date(meeting.meeting_date).toLocaleDateString()} 
                                {meeting.meeting_time && ` at ${meeting.meeting_time}`}
                              </div>
                              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                                📍 {meeting.venue}
                              </div>
                              <div style={{ fontSize: '14px', color: '#666' }}>
                                Type: {meeting.meeting_type}
                              </div>
                              {meeting.agenda && (
                                <div style={{
                                  marginTop: '10px',
                                  padding: '10px',
                                  background: 'white',
                                  borderRadius: '5px',
                                  fontSize: '13px',
                                  whiteSpace: 'pre-line'
                                }}>
                                  <strong>Agenda:</strong><br/>
                                  {meeting.agenda}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '140px' }}>
                              <span className="meeting-status" style={{
                                background: getStatusColor(meeting.status),
                                textAlign: 'center'
                              }}>
                                {meeting.status?.toUpperCase()}
                              </span>
                              {isAdmin && meeting.status === 'scheduled' && (
                                <>
                                  <button
                                    onClick={() => sendMeetingInvite(meeting)}
                                    className="btn-whatsapp"
                                    style={{
                                      padding: '8px 15px',
                                      background: '#25D366',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '5px',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '5px'
                                    }}
                                  >
                                    📱 Send Invite
                                  </button>
                                  <button
                                    onClick={() => handleStartMeeting(meeting)}
                                    className="btn-success"
                                    style={{
                                      padding: '8px 15px',
                                      background: '#28a745',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '5px',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      fontWeight: '500'
                                    }}
                                  >
                                    Start Meeting
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ textAlign: 'center', color: '#999' }}>No upcoming meetings scheduled</p>
                  )}
                </div>
              )}

              {/* Past Meetings - YOUR ORIGINAL CODE */}
              {activeTab === 'past' && (
                <div className="dashboard-card">
                  <h3 style={{ marginBottom: '20px' }}>Past Meetings</h3>
                  {pastMeetings.length > 0 ? (
                    <div style={{ display: 'grid', gap: '15px' }}>
                      {pastMeetings.map(meeting => (
                        <div key={meeting.id} className="meeting-item">
                          <h4 style={{ margin: '0 0 10px 0' }}>{meeting.title}</h4>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            📅 {new Date(meeting.meeting_date).toLocaleDateString()}
                          </div>
                          {meeting.minutes && (
                            <div style={{
                              marginTop: '10px',
                              padding: '10px',
                              background: '#f8f9fa',
                              borderRadius: '5px',
                              fontSize: '13px',
                              whiteSpace: 'pre-line'
                            }}>
                              <strong>Minutes:</strong><br/>
                              {meeting.minutes.substring(0, 200)}...
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ textAlign: 'center', color: '#999' }}>No past meetings</p>
                  )}
                </div>
              )}

              {/* Action Items - YOUR ORIGINAL CODE */}
              {activeTab === 'actions' && (
                <div className="dashboard-card">
                  <h3 style={{ marginBottom: '20px' }}>Action Items</h3>
                  {actionItems.length > 0 ? (
                    <div style={{ display: 'grid', gap: '15px' }}>
                      {actionItems.map(item => (
                        <div key={item.id} style={{
                          padding: '15px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          borderLeft: `4px solid ${getActionItemStatusColor(item.status)}`
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', marginBottom: '5px' }}>
                                {item.description}
                              </div>
                              <div style={{ fontSize: '13px', color: '#666' }}>
                                From: {item.meetings?.title} ({new Date(item.meetings?.meeting_date).toLocaleDateString()})
                              </div>
                              <div style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                                Assigned to: {item.assigned_to || 'All'} | 
                                Priority: {item.priority?.toUpperCase()}
                                {item.due_date && ` | Due: ${new Date(item.due_date).toLocaleDateString()}`}
                              </div>
                            </div>
                            <span style={{
                              padding: '5px 10px',
                              borderRadius: '5px',
                              background: getActionItemStatusColor(item.status),
                              color: 'white',
                              fontSize: '12px'
                            }}>
                              {item.status?.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ textAlign: 'center', color: '#999' }}>No action items</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Meeting Scheduler Modal */}
      {showScheduler && (
        <MeetingScheduler
          userData={userData}
          onClose={() => setShowScheduler(false)}
          onSave={() => {
            setShowScheduler(false);
            fetchMeetings();
          }}
        />
      )}
    </div>
  );
}

export default MeetingManagement;